/**
 * Competition-configurable vocabulary helpers.
 * Prefer config labels over hard-coded Campus/Team so CCSF can use
 * Department/Division without breaking university Challenges.
 */
import { Competition, CompetitionConfig, Entity } from '../userApi';

export const getCompetitionConfig = (competition?: Competition | null): CompetitionConfig =>
  (competition && competition.config) || {};

export const campusLabelFor = (competition: Competition | null | undefined, fallback = 'Campus'): string => {
  const config = getCompetitionConfig(competition);
  return (config.campusLabel && String(config.campusLabel)) || fallback;
};

export const teamLabelFor = (competition: Competition | null | undefined, fallback = 'Team'): string => {
  const config = getCompetitionConfig(competition);
  return (config.teamLabel && String(config.teamLabel)) || fallback;
};

export const teamsLabelFor = (competition: Competition | null | undefined, fallback = 'Teams'): string => {
  const config = getCompetitionConfig(competition);
  if (config.teamsLabel) {
    return String(config.teamsLabel);
  }
  if (config.teamLabel) {
    return `${config.teamLabel}s`;
  }
  return fallback;
};

export const campusesLabelFor = (competition: Competition | null | undefined, fallback = 'Campuses'): string => {
  const config = getCompetitionConfig(competition);
  if (config.campusesLabel) {
    return String(config.campusesLabel);
  }
  const singular = campusLabelFor(competition, '');
  return singular ? `${singular}s` : fallback;
};

export const hostEntityIdFor = (competition: Competition | null | undefined): number => {
  const config = getCompetitionConfig(competition);
  return Number(config.hostEntityId || 0);
};

export const isHostEntity = (entity: Entity | null | undefined, competition?: Competition | null): boolean => {
  if (!entity || !entity.id) {
    return false;
  }
  const hostId = hostEntityIdFor(competition);
  if (hostId && Number(entity.id) === hostId) {
    return true;
  }
  return entity.name === 'City and County of San Francisco';
};

/** Internal ops entities (e.g. Admin / Admin - Admin) excluded from public scoreboards. */
export const isHiddenFromScoreboard = (
  entity: Entity | null | undefined,
  competition?: Competition | null
): boolean => {
  if (!entity || !entity.id) {
    return false;
  }
  const config = getCompetitionConfig(competition);
  const hiddenIds = (config.hiddenFromScoreboardEntityIds || []).map((id) => Number(id));
  if (hiddenIds.includes(Number(entity.id))) {
    return true;
  }
  const name = (entity.name || '').trim();
  return name === 'Admin' || name === 'Admin - Admin';
};

export const departmentDivisionFromEntity = (entity: Entity | null | undefined): { department: string; division: string } => {
  const name = (entity && entity.name) || '';
  const md = (entity && entity.descriptionMD) || '';
  const marker = 'ccsf_meta:';
  const idx = md.indexOf(marker);
  if (idx >= 0) {
    let rest = md.slice(idx + marker.length);
    const end = rest.indexOf('-->');
    if (end >= 0) {
      rest = rest.slice(0, end).trim();
    }
    try {
      const meta = JSON.parse(rest);
      if (meta && meta.department) {
        return {
          department: String(meta.department),
          division: meta.division ? String(meta.division) : name
        };
      }
    } catch (_err) {
      // Fall through to name parsing.
    }
  }
  if (name === 'Other') {
    return { department: 'Other', division: 'Other' };
  }
  const split = name.indexOf(' - ');
  if (split >= 0) {
    return { department: name.slice(0, split), division: name.slice(split + 3) };
  }
  return { department: '', division: name };
};

export const entityPageTitle = (entity: Entity | null | undefined): string => {
  if (!entity) {
    return '';
  }
  if (entity.type === Entity.TypeEnum.Team) {
    return departmentDivisionFromEntity(entity).division || entity.name;
  }
  return entity.name;
};

export const teamCreationDisabled = (competition: Competition | null | undefined): boolean => {
  const config = getCompetitionConfig(competition);
  return Boolean(config.teamCreationDisabled) || Boolean(config.hideUniversityLabels && config.customSignupTeamIds && config.customSignupTeamIds.length);
};

export const logoPendingApproval = (competition: Competition | null | undefined): boolean => {
  const config = getCompetitionConfig(competition);
  return Boolean(config.logoPendingApproval);
};
